import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, AlertTriangle, Search } from "lucide-react";
import { Service } from "@/hooks/useServices";
import { Room } from "@/hooks/useRooms";
import { format, eachDayOfInterval } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useBulkDeleteTimeSlots } from "@/hooks/useTimeSlots";

interface BulkDeleteTimeSlotsDialogProps {
  providerId: string;
  services: Service[];
  rooms: Room[];
  onTimeSlotsDeleted?: () => void;
}

interface BulkDeleteFormData {
  service_id: string;
  room_id: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
}

interface PreviewResult {
  deletableIds: string[];
  skippedCount: number;
  totalFound: number;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const BulkDeleteTimeSlotsDialog = ({ providerId, services, rooms, onTimeSlotsDeleted }: BulkDeleteTimeSlotsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const bulkDelete = useBulkDeleteTimeSlots();
  const { toast } = useToast();

  const form = useForm<BulkDeleteFormData>();

  const handleDayToggle = (dayValue: number, checked: boolean) => {
    if (checked) {
      setSelectedDays(prev => [...prev, dayValue]);
    } else {
      setSelectedDays(prev => prev.filter(day => day !== dayValue));
    }
    setPreview(null);
  };

  const handleSearch = useCallback(async () => {
    const data = form.getValues();
    if (!data.service_id || !data.start_date || !data.end_date || !data.start_time || !data.end_time || selectedDays.length === 0) {
      toast({ title: "Missing fields", description: "Please fill in all fields and select at least one day.", variant: "destructive" });
      return;
    }

    setIsSearching(true);
    setPreview(null);
    setConfirmText("");

    try {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      const filteredDays = allDays.filter(day => selectedDays.includes(day.getDay()));

      if (filteredDays.length === 0) {
        setPreview({ deletableIds: [], skippedCount: 0, totalFound: 0 });
        setIsSearching(false);
        return;
      }

      const startDateStr = format(startDate, 'yyyy-MM-dd') + `T${data.start_time}:00Z`;
      const endDateStr = format(endDate, 'yyyy-MM-dd') + `T${data.end_time}:00Z`;

      let query = supabase
        .from('time_slots')
        .select('id, start_time, current_bookings')
        .eq('provider_id', providerId)
        .eq('service_id', data.service_id)
        .gte('start_time', startDateStr)
        .lte('start_time', endDateStr);

      if (data.room_id && data.room_id !== "any") {
        query = query.eq('room_id', data.room_id);
      }

      const { data: slots, error } = await query;

      if (error) throw error;

      // Client-side filter by day-of-week and time range
      const matchingSlots = (slots || []).filter(slot => {
        const slotDate = new Date(slot.start_time);
        const dayMatch = selectedDays.includes(slotDate.getDay());
        const slotTimeStr = format(slotDate, 'HH:mm');
        const timeMatch = slotTimeStr >= data.start_time && slotTimeStr <= data.end_time;
        return dayMatch && timeMatch;
      });

      const deletable = matchingSlots.filter(s => s.current_bookings === 0);
      const skipped = matchingSlots.filter(s => s.current_bookings > 0);

      setPreview({
        deletableIds: deletable.map(s => s.id),
        skippedCount: skipped.length,
        totalFound: matchingSlots.length,
      });
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to search time slots", variant: "destructive" });
    } finally {
      setIsSearching(false);
    }
  }, [form, providerId, selectedDays, toast]);

  const handleDelete = async () => {
    if (!preview || preview.deletableIds.length === 0) return;

    setIsDeleting(true);
    try {
      await bulkDelete.mutateAsync(preview.deletableIds);
      toast({
        title: "Bulk delete complete",
        description: `Deleted ${preview.deletableIds.length} time slot${preview.deletableIds.length !== 1 ? 's' : ''}${preview.skippedCount > 0 ? `, skipped ${preview.skippedCount} with bookings` : ''}.`,
      });
      form.reset();
      setSelectedDays([]);
      setPreview(null);
      setConfirmText("");
      setOpen(false);
      onTimeSlotsDeleted?.();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to delete time slots", variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteCount = preview?.deletableIds.length || 0;
  const confirmRequired = `Delete ${deleteCount} slots`;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setPreview(null); setConfirmText(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="text-destructive border-destructive/50 hover:bg-destructive/10">
          <Trash2 className="h-4 w-4 mr-2" />
          Bulk Delete Slots
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-destructive">Bulk Delete Time Slots</DialogTitle>
        </DialogHeader>

        <Alert variant="destructive" className="border-destructive/30 bg-destructive/5">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>
            Time slots with existing bookings will <strong>not</strong> be deleted. Only empty slots matching your criteria will be removed.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="service_id"
              rules={{ required: "Please select a service" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service</FormLabel>
                  <Select onValueChange={(v) => { field.onChange(v); setPreview(null); }} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="room_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Room (optional)</FormLabel>
                  <Select onValueChange={(v) => { field.onChange(v); setPreview(null); }} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Any room" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="any">Any room</SelectItem>
                      {rooms.map((room) => (
                        <SelectItem key={room.id} value={room.id}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} onChange={(e) => { field.onChange(e); setPreview(null); }} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} onChange={(e) => { field.onChange(e); setPreview(null); }} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-3">
              <FormLabel>Select Days of Week</FormLabel>
              <div className="grid grid-cols-2 gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`del-day-${day.value}`}
                      checked={selectedDays.includes(day.value)}
                      onCheckedChange={(checked) => handleDayToggle(day.value, checked as boolean)}
                    />
                    <label htmlFor={`del-day-${day.value}`} className="text-sm font-medium">
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
              {selectedDays.length === 0 && (
                <p className="text-sm text-destructive">Please select at least one day</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_time"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} onChange={(e) => { field.onChange(e); setPreview(null); }} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_time"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} onChange={(e) => { field.onChange(e); setPreview(null); }} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Search Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleSearch}
              disabled={isSearching || selectedDays.length === 0}
            >
              <Search className="h-4 w-4 mr-2" />
              {isSearching ? "Searching..." : "Find Matching Slots"}
            </Button>

            {/* Preview Results */}
            {preview && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <h4 className="font-medium">Results</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Total matching slots:</strong> {preview.totalFound}</p>
                  <p className="text-destructive"><strong>Slots to delete:</strong> {preview.deletableIds.length}</p>
                  {preview.skippedCount > 0 && (
                    <p className="text-amber-600"><strong>Skipped (have bookings):</strong> {preview.skippedCount}</p>
                  )}
                </div>

                {deleteCount > 0 && (
                  <div className="pt-3 space-y-2 border-t">
                    <p className="text-sm text-muted-foreground">
                      Type <strong>"{confirmRequired}"</strong> to confirm:
                    </p>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={confirmRequired}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              {preview && deleteCount > 0 && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isDeleting || confirmText !== confirmRequired}
                >
                  {isDeleting ? "Deleting..." : `Delete ${deleteCount} Slots`}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkDeleteTimeSlotsDialog;
